<template>
  <select-dict
    v-model="glassThicknesses"
    :selectList="filteredGlassThicknessesList"
    :select-first-item="true"
  />
</template>

<script setup lang="ts">
import { defineModel, watch, ref, computed, onMounted } from "vue";
import SelectDict from "@/components/UI/SelectDict.vue";

import { useDictStore } from "@/stores/dictStore";
import { usePartitionStore } from "@/stores/new/partition.store";
import { useNewEstimateStore } from "@/stores/new/estimateStore";
import { useDoorsStore } from "@/stores/new/doors.store";
import { useOverdoorStore } from "@/stores/new/overdoor.store";

type GlassThicknessesItem = {
  code: string;
  [key: string]: any;
};

const partitionsStore = usePartitionStore();
const estimateStore = useNewEstimateStore();
const dictStore = useDictStore();
const doorsStore = useDoorsStore();
const overdoorStore = useOverdoorStore();

const activeGlassThinknessDoor = computed(
  () => doorsStore.activeGlassThinknessDoor,
);
const activeGlassThinknessOverdoor = computed(
  () => overdoorStore.activeGlassThinknessOverdoor,
);

const systemCode = computed(() => estimateStore.estimate.installedSystemCode);
const glassThicknesses = defineModel<string>();
const filteredGlassThicknessesList = ref<GlassThicknessesItem[]>([]);

const getActualGlassThicknessList = () => {
  const availableGlassThicknesses = dictStore.findGlassSortThicknesses(
    partitionsStore.activeGlassPartition?.structureType,
  );
  if (!availableGlassThicknesses?.thicknesses) {
    filteredGlassThicknessesList.value = [];
    return;
  }

  const filteredThicknesses = availableGlassThicknesses.thicknesses
    ?.map((item) => dictStore.findGlassThickness(item, systemCode.value))
    .filter((glassThicknesses) => !!glassThicknesses);

  filteredGlassThicknessesList.value = filteredThicknesses ?? [];
};

watch(
  () => [partitionsStore.activeGlassPartition?.structureType],
  () => {
    getActualGlassThicknessList();
  },
  {
    immediate: true,
    deep: true,
  },
);

watch(
  () => glassThicknesses.value,
  (currentThick) => {
    activeGlassThinknessDoor.value?.update({ structureType: currentThick });
    activeGlassThinknessOverdoor.value?.update({ structureType: currentThick });
  },
  {
    immediate: true,
    deep: true,
  },
);

onMounted(() => {
  const defaultGlassThickList = dictStore.getGlassThickness(systemCode.value);
  filteredGlassThicknessesList.value = defaultGlassThickList;
});
</script>

<style lang="scss" scoped></style>
